import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import BugReportIcon from "@mui/icons-material/BugReport";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Log error to monitoring service in production
    if (process.env.NODE_ENV === "production") {
      // Add your error reporting service here
      console.error("Production error:", {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  private handleRefresh = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    window.location.reload();
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
            p: 3,
          }}
        >
          <Card sx={{ maxWidth: 600, width: "100%" }}>
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <BugReportIcon
                sx={{ fontSize: 64, color: "error.main", mb: 2 }}
              />
              
              <Typography variant="h5" gutterBottom color="error">
                Something went wrong
              </Typography>
              
              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                We encountered an unexpected error. Please try refreshing the page
                or contact support if the problem persists.
              </Typography>

              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="body2" fontWeight="bold">
                  Error Details:
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {this.state.error?.message || "Unknown error occurred"}
                </Typography>
              </Alert>

              <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                >
                  Refresh Page
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>
              </Box>

              {process.env.NODE_ENV === "development" && this.state.errorInfo && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ textAlign: "left" }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Development Error Details:
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: "0.75rem",
                        maxHeight: 200,
                      }}
                    >
                      {this.state.error?.stack}
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#f5f5f5",
                        p: 2,
                        borderRadius: 1,
                        overflow: "auto",
                        fontSize: "0.75rem",
                        maxHeight: 200,
                        mt: 1,
                      }}
                    >
                      {this.state.errorInfo.componentStack}
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;